import { REACT_APP_MAP_KEY } from 'app/config/map-key';
import { FENCE_RISK_COLORS, RiskClassification, DEFAULT_FENCE_RISK_CLASSIFICATION, DEFAULT_FENCE_NOTIFICATION_ENABLED, FENCE_RISK_NOTIFICATION_DEFAULT } from 'app/config/constants';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { createEntity, getEntity, reset, updateEntity } from 'app/entities/virtual-fence-circle/virtual-fence-circle.reducer';
import { getEntities as getCircleEntities } from 'app/entities/virtual-fence-circle/virtual-fence-circle.reducer';
import { getEntitiesForMap as getPolygonEntities } from 'app/entities/virtual-fence-polygon/virtual-fence-polygon.reducer';
import {
  getStorageOptions,
  IdStorageTypes,
  setStorageOptions,
  ValuesStorageTypes,
} from 'app/modules/dashboards/dashboard-truck-implement/components/truck-implement-journey-tab/storageOptions';
import { IPosition } from 'app/modules/dashboards/dashboard-truck-implement/interface/dashboardTruckImplementInterface';
import { AutoCompleteSearch } from 'app/shared/components/autocomplete-search/AutoCompleteSearch';
import { mapStyle } from 'app/shared/layout/maps/CustomStyle';
import { IVirtualFenceCircle, VirtualFenceSyncStatus } from 'app/shared/model/virtual-fence-circle.model';
import { IVirtualFencePolygon } from 'app/shared/model/virtual-fence-polygon.model';
import { ITruckGroup } from 'app/shared/model/truck-group.model';
import { Box, IconButton, Tooltip, useTheme } from '@mui/material';
import LayersRoundedIcon from '@mui/icons-material/LayersRounded';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { GoogleMap, withGoogleMap, withScriptjs } from 'react-google-maps';
import Circle from 'react-google-maps/lib/components/Circle';
import PolygonComponent from 'react-google-maps/lib/components/Polygon';
import StreetViewPanorama from 'react-google-maps/lib/components/StreetViewPanorama';
import { toast } from 'react-toastify';
import { Spinner } from 'reactstrap';
import { compose, withProps } from 'recompose';
import { VirtualFenceForms } from './VirtualFenceForms';

const MIN_VIRTUAL_FENCE_RADIUS_METERS = 30;
const MAX_VIRTUAL_FENCE_RADIUS_METERS = 65500;

const normalizeVirtualFenceRadius = (value?: number) =>
  Math.min(Math.max(value ?? 250, MIN_VIRTUAL_FENCE_RADIUS_METERS), MAX_VIRTUAL_FENCE_RADIUS_METERS);

const getFenceCircleOptions = (
  syncStatus: VirtualFenceSyncStatus | null | undefined,
  color: string,
  statusColors: { pending: string; pendingDelete: string }
) => {
  if (syncStatus === 'PENDING_SYNC') {
    return { fillColor: statusColors.pending, fillOpacity: 0.2, strokeColor: statusColors.pending, strokeWeight: 2, zIndex: 0 };
  }
  if (syncStatus === 'PENDING_DELETE') {
    return { fillColor: statusColors.pendingDelete, fillOpacity: 0.15, strokeColor: statusColors.pendingDelete, strokeWeight: 2, zIndex: 0 };
  }
  return { fillColor: color, fillOpacity: 0.4, strokeColor: color, strokeWeight: 1, zIndex: 0 };
};

const isPendingFenceSync = (syncStatus?: VirtualFenceSyncStatus | null) =>
  syncStatus === 'PENDING_SYNC' || syncStatus === 'PENDING_DELETE';

const VirtualFenceMap = ({ props }) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const initialPosition = { lat: -15.79956, lng: -47.90032 };
  const [zoom] = useState<number>(4.6);
  const [isNew] = useState(!props.match.params || !props.match.params.id);
  const [riskClassification, setRiskClassificationState] = useState<RiskClassification>(DEFAULT_FENCE_RISK_CLASSIFICATION);
  const [notificationEnabled, setNotificationEnabledState] = useState<boolean>(DEFAULT_FENCE_NOTIFICATION_ENABLED);
  const notificationManuallyChangedRef = useRef(false);
  const [radius, setRadius] = useState<number>(250);
  const [description, setDescription] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [latVirtualFence, setLatVirtualFence] = useState(initialPosition.lat);
  const [lngVirtualFence, setLngVirtualFence] = useState(initialPosition.lng);
  const [isAutoComplete, setIsAutoComplete] = useState<boolean>(false);
  const [center] = useState<IPosition>({ lat: latVirtualFence, lng: lngVirtualFence });
  const [mapType, setMapType] = useState<ValuesStorageTypes>(getStorageOptions(IdStorageTypes.MAP_TYPE) ?? 'MAP');
  const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([]);

  const setRiskClassification = (v: RiskClassification) => {
    setRiskClassificationState(v);
    if (!notificationManuallyChangedRef.current) {
      setNotificationEnabledState(FENCE_RISK_NOTIFICATION_DEFAULT[v]);
    }
  };

  const setNotificationEnabled = (v: boolean) => {
    notificationManuallyChangedRef.current = true;
    setNotificationEnabledState(v);
  };

  const loading = useAppSelector(state => state.virtualFenceCircle.loading);
  const updateSuccess = useAppSelector(state => state.virtualFenceCircle.updateSuccess);
  const mapRef = useRef(null);
  const shouldFitBoundsRef = useRef(true);
  const virtualFenceCircleList = useAppSelector(state => state.virtualFenceCircle.entities) as IVirtualFenceCircle[];
  const virtualFenceCircleEntity = useAppSelector(state => state.virtualFenceCircle.entity) as IVirtualFenceCircle;
  const allPolygons = useAppSelector(state => state.virtualFencePolygon.entities) as IVirtualFencePolygon[];
  const [showAllFences, setShowAllFences] = useState(false);
  const statusColors = {
    pending: theme.palette.warning.main,
    pendingDelete: theme.palette.error.main,
  };
  const readOnlyFromList = new URLSearchParams(props.location.search).get('readOnlyPending') === 'true';
  const readOnly = !isNew && (readOnlyFromList || isPendingFenceSync(virtualFenceCircleEntity?.syncStatus));

  const handleClose = () => {
    dispatch(reset());
    const returnTo = props.location.state?.returnTo;
    if (returnTo === '/reports/passages') props.history.replace(returnTo);
    else props.history.goBack();
  };

  function onClickMapType() {
    document.getElementsByClassName('gm-style-mtc')[0].addEventListener('click', () => {
      setMapType(ValuesStorageTypes.MAP);
      setStorageOptions(IdStorageTypes.MAP_TYPE, ValuesStorageTypes.MAP);
    });
    document.getElementsByClassName('gm-style-mtc')[1].addEventListener('click', () => {
      setMapType(ValuesStorageTypes.SATELLITE);
      setStorageOptions(IdStorageTypes.MAP_TYPE, ValuesStorageTypes.SATELLITE);
    });
  }

  const saveEntity = values => {
    if (readOnly) {
      return;
    }
    if (!latVirtualFence || !lngVirtualFence) {
      return toast.error('É necessário selecionar um local no mapa');
    }
    const nextRadius = values.radius ?? radius;
    if (nextRadius < MIN_VIRTUAL_FENCE_RADIUS_METERS) {
      return toast.error(`O raio mínimo da cerca é de ${MIN_VIRTUAL_FENCE_RADIUS_METERS} metros`);
    }
    if (nextRadius > MAX_VIRTUAL_FENCE_RADIUS_METERS) {
      return toast.error(`O raio máximo da cerca é de ${MAX_VIRTUAL_FENCE_RADIUS_METERS} metros`);
    }
    const entity = {
      ...virtualFenceCircleEntity,
      ...values,
      latitude: latVirtualFence,
      longitude: lngVirtualFence,
      radius: normalizeVirtualFenceRadius(nextRadius),
      activated: true,
      truckGroups: selectedGroupIds.map(id => ({ id })),
      ...(isNew && new URLSearchParams(props.location.search).get('companyId')
        ? { company: { id: Number(new URLSearchParams(props.location.search).get('companyId')) } }
        : {}),
    };
    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const badgeBg = theme.palette.background.paper;
  const badgeColor = theme.palette.text.primary;
  const badgeBorder = theme.palette.divider;

  const renderLoading = useCallback(
    () => loading && (<div className="spinner-map"><Spinner color="info" /></div>),
    [loading]
  );

  const setFitBounds = () => {
    if (!shouldFitBoundsRef.current || !mapRef.current || !(window as any).google?.maps) return;
    const bounds = new (window as any).google.maps.LatLngBounds();
    const listBounds = [];
    if (latVirtualFence && lngVirtualFence && radius && isAutoComplete) {
      listBounds.push(
        { lat: latVirtualFence, lng: lngVirtualFence },
        { lat: latVirtualFence + (radius / 300) * 0.0089, lng: lngVirtualFence + (radius / 300) * 0.0089 },
        { lat: latVirtualFence - (radius / 300) * 0.0089, lng: lngVirtualFence - (radius / 300) * 0.0089 }
      );
    } else if (virtualFenceCircleEntity?.latitude === latVirtualFence && virtualFenceCircleEntity?.longitude === lngVirtualFence && radius) {
      listBounds.push(
        { lat: virtualFenceCircleEntity.latitude, lng: virtualFenceCircleEntity.longitude },
        { lat: virtualFenceCircleEntity.latitude + (radius / 300) * 0.0089, lng: virtualFenceCircleEntity.longitude + (radius / 300) * 0.0089 },
        { lat: virtualFenceCircleEntity.latitude - (radius / 300) * 0.0089, lng: virtualFenceCircleEntity.longitude - (radius / 300) * 0.0089 }
      );
    }
    listBounds.filter(item => item.lat && item.lng).forEach(item => bounds.extend({ lat: item.lat, lng: item.lng }));
    if (listBounds.length) { mapRef.current.fitBounds(bounds); shouldFitBoundsRef.current = false; }
  };

  const renderExistingFences = () => {
    if (!showAllFences) return null;
    const editingId = isNew ? null : virtualFenceCircleEntity?.id;
    const circles = virtualFenceCircleList
      .filter(fence => fence.latitude != null && fence.longitude != null && fence.activated && fence.id !== editingId)
      .map(fence => {
        const color = fence.color ?? FENCE_RISK_COLORS[DEFAULT_FENCE_RISK_CLASSIFICATION];
        return (
          <Circle
            key={`c-${fence.id}`}
            center={{ lat: fence.latitude, lng: fence.longitude }}
            radius={normalizeVirtualFenceRadius(fence.radius)}
            options={{ fillColor: color, fillOpacity: 0.25, strokeColor: color, strokeOpacity: 0.8, strokeWeight: 0.8, zIndex: 0 }}
          />
        );
      });
    const polygons = allPolygons
      .filter(fence => fence.activated && fence.points?.length >= 3)
      .map(fence => {
        const path = [...(fence.points ?? [])]
          .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
          .filter((p): p is typeof p & { latitude: number; longitude: number } => p.latitude != null && p.longitude != null)
          .map(p => ({ lat: p.latitude, lng: p.longitude }));
        if (path.length < 3) return null;
        const color = fence.color ?? FENCE_RISK_COLORS[DEFAULT_FENCE_RISK_CLASSIFICATION];
        return (
          <PolygonComponent
            key={`p-${fence.id}`}
            path={path}
            options={{ fillColor: color, fillOpacity: 0.25, strokeColor: color, strokeOpacity: 0.8, strokeWeight: 0.8, zIndex: 0 }}
          />
        );
      });
    return <>{circles}{polygons}</>;
  };

  const renderEditingCircle = () => {
    const currentRadius = normalizeVirtualFenceRadius(radius || MIN_VIRTUAL_FENCE_RADIUS_METERS);
    const currentColor = FENCE_RISK_COLORS[riskClassification] ?? FENCE_RISK_COLORS[DEFAULT_FENCE_RISK_CLASSIFICATION];
    return (
      <Circle
        center={{ lat: latVirtualFence, lng: lngVirtualFence }}
        radius={currentRadius}
        draggable={!readOnly}
        onDragEnd={readOnly ? undefined : e => [setLatVirtualFence(e.latLng.lat()), setLngVirtualFence(e.latLng.lng())]}
        options={{ fillColor: currentColor, fillOpacity: 0.5, zIndex: 1, strokeWeight: 2, strokeColor: currentColor }}
      />
    );
  };

  const setEditValues = useCallback(() => {
    setLngVirtualFence(virtualFenceCircleEntity.longitude ?? initialPosition.lng);
    setLatVirtualFence(virtualFenceCircleEntity.latitude ?? initialPosition.lat);
    setDescription(virtualFenceCircleEntity.description ?? '');
    setRiskClassificationState(virtualFenceCircleEntity.riskClassification ?? DEFAULT_FENCE_RISK_CLASSIFICATION);
    setNotificationEnabledState(virtualFenceCircleEntity.notificationEnabled ?? false);
    notificationManuallyChangedRef.current = true;
    setRadius(normalizeVirtualFenceRadius(virtualFenceCircleEntity.radius));
    setName(virtualFenceCircleEntity.name ?? '');
    if (virtualFenceCircleEntity.truckGroups) {
      setSelectedGroupIds(virtualFenceCircleEntity.truckGroups.map((g: ITruckGroup) => g.id));
    }
  }, [virtualFenceCircleEntity]);

  useEffect(() => {
    if (isNew) { dispatch(reset()); } else { dispatch(getEntity(props.match.params.id)); }
  }, []);

  useEffect(() => {
    if (showAllFences) {
      dispatch(getCircleEntities({}));
      dispatch(getPolygonEntities());
    }
  }, [showAllFences]);

  useEffect(() => {
    if (!isNew && virtualFenceCircleEntity?.id) { shouldFitBoundsRef.current = true; setFitBounds(); }
  }, [virtualFenceCircleEntity?.id, radius]);

  useEffect(() => {
    if (isAutoComplete) { shouldFitBoundsRef.current = true; setFitBounds(); }
  }, [isAutoComplete, latVirtualFence, lngVirtualFence, radius]);

  useEffect(() => { !isNew && setEditValues(); }, [setEditValues]);
  useEffect(() => { if (updateSuccess) handleClose(); }, [updateSuccess]);

  const handleMapClick = event => {
    shouldFitBoundsRef.current = false;
    setIsAutoComplete(false);
    setLatVirtualFence(event.latLng.lat());
    setLngVirtualFence(event.latLng.lng());
  };

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '400px minmax(0, 1fr)', xl: '420px minmax(0, 1fr)' }, gap: 1.5, alignItems: 'stretch' }}>
      <Box sx={{ bgcolor: 'background.paper', color: 'text.primary', border: '1px solid', borderColor: 'divider', borderRadius: '8px', p: { xs: 1.5, sm: 2.25 }, height: { xs: 'auto', md: '72vh' }, overflowY: 'auto', minWidth: 0 }}>
        <VirtualFenceForms
          saveEntity={saveEntity}
          name={name}
          setName={setName}
          description={description}
          setDescription={setDescription}
          radius={radius}
          setRadius={setRadius}
          riskClassification={riskClassification}
          setRiskClassification={setRiskClassification}
          notificationEnabled={notificationEnabled}
          setNotificationEnabled={setNotificationEnabled}
          selectedGroupIds={selectedGroupIds}
          setSelectedGroupIds={setSelectedGroupIds}
          minRadius={MIN_VIRTUAL_FENCE_RADIUS_METERS}
          maxRadius={MAX_VIRTUAL_FENCE_RADIUS_METERS}
          readOnly={readOnly}
        />
      </Box>

      <Box sx={{ position: 'relative', height: { xs: '55vh', md: '72vh' }, minHeight: 360, border: '1px solid', borderColor: 'divider', borderRadius: '8px', overflow: 'hidden', minWidth: 0 }}>
        <Box sx={{ position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)', zIndex: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 320, bgcolor: 'background.paper', borderRadius: '10px', boxShadow: 4 }}>
            <AutoCompleteSearch
              setLatitude={setLatVirtualFence}
              setLongitude={setLngVirtualFence}
              setIsAutoComplete={setIsAutoComplete}
              disabled={readOnly}
            />
          </Box>
          <Tooltip title={showAllFences ? 'Ocultar cercas existentes' : 'Exibir cercas existentes'} placement="bottom">
            <IconButton
              onClick={() => setShowAllFences(v => !v)}
              sx={{
                bgcolor: showAllFences ? 'primary.main' : 'background.paper',
                color: showAllFences ? 'primary.contrastText' : 'text.primary',
                boxShadow: 3, border: '1px solid', borderColor: 'divider', flexShrink: 0,
                '&:hover': { bgcolor: showAllFences ? 'primary.dark' : 'action.hover' },
              }}
            >
              <LayersRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Box role="status" sx={{ position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)', zIndex: 2, display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.75, borderRadius: '6px', bgcolor: badgeBg, color: badgeColor, border: '1px solid', borderColor: badgeBorder, boxShadow: 3, fontSize: '0.78rem', fontWeight: 400, lineHeight: 1.35, width: 'max-content', maxWidth: 'calc(100% - 100px)', textAlign: 'center', pointerEvents: 'none' }}>
          <span>{readOnly ? 'Cerca pendente de sincronização. Os campos estão bloqueados para edição.' : 'Clique no mapa para posicionar a cerca. Arraste o círculo para ajustar.'}</span>
        </Box>

        <VirtualFenceGoogleMap
          mapRef={mapRef}
          zoom={zoom}
          center={center}
          mapType={mapType}
          loading={loading}
          onTilesLoaded={setFitBounds}
          onMapTypeIdChanged={onClickMapType}
          onClick={readOnly ? undefined : handleMapClick}
          renderLoading={renderLoading}
          renderExistingFences={renderExistingFences}
          renderEditingCircle={renderEditingCircle}
        />
      </Box>
    </Box>
  );
};

const VirtualFenceGoogleMapComponent = ({ mapRef, zoom, center, mapType, loading, onTilesLoaded, onMapTypeIdChanged, onClick, renderLoading, renderExistingFences, renderEditingCircle }) => (
  <GoogleMap ref={mapRef} zoom={zoom} center={center} defaultOptions={{ styles: mapStyle }} onTilesLoaded={onTilesLoaded} onMapTypeIdChanged={onMapTypeIdChanged} mapTypeId={google.maps.MapTypeId[mapType]} onClick={onClick}>
    <StreetViewPanorama visible={false} />
    {renderLoading()}
    {!loading && renderExistingFences()}
    {!loading && renderEditingCircle()}
  </GoogleMap>
);

const G_API_URL = `https://maps.googleapis.com/maps/api/js?key=${REACT_APP_MAP_KEY}&&v=3.exp&libraries=geometry,drawing,places`;

const VirtualFenceGoogleMap = compose(
  withProps({ googleMapURL: G_API_URL, loadingElement: <div style={{ height: `100%` }} />, containerElement: <div style={{ height: `100%` }} />, mapElement: <div style={{ height: `100%` }} /> }),
  withScriptjs,
  withGoogleMap
)(VirtualFenceGoogleMapComponent);

export default VirtualFenceMap;
